import React, { useState, useEffect, useCallback } from 'react';
import { Box, LinearProgress, IconButton, Stack, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography, Button } from '@mui/material';
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { DataGridPro, DataGridProProps } from "@mui/x-data-grid-pro";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GridColDef, GridToolbarFilterButton, GridToolbarContainer, 
    getGridStringOperators, GridToolbarColumnsButton, GridToolbarDensitySelector, GridToolbarExport
} from '@mui/x-data-grid';
import { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro';
import { RiExpandUpDownLine } from "react-icons/ri";
import { apiClient } from '../helper/api';
import { FaDownload, FaPlus, FaRegEye, FaTrash, FaUpload } from 'react-icons/fa';
import CancelIcon from "@mui/icons-material/Cancel";
import { getRowsPerPageOptions } from '../components/AddEdit/CustomFunctions';
import { DashboardButton } from '../components/Elements/DashboardTile';
import Swal from 'sweetalert2';
import { StyledDropzone } from '../components/Elements/Dropzone';
import useAuth from '../context/AuthProvider';
import { AddReportTag } from '../components/AddEdit/AddReportTag';
import { safeMoment } from '../helper/utils';
import JSZip from 'jszip';
import { LightBlueBtnColor, GreenBtnColor, OrangeBtnColor, PrimaryBlueColor } from '../helper/theme';
import { UploadReportTagDocuments } from '../components/Elements/UploadReportTagDocuments';
import { DocumentViewer } from '../components/Elements/DocumentViewer';

export interface ReportTagModel {
    reportTagId?: number;
    tagNumber: string;
    state: string;
    stateId?: number;
    isMultipleTags?: boolean;
    insuredName?: string;
    invoiceNumber?: string;
    policyNumber?: string;
    partnerId: number;
    agency: string;  // agency/client/partner name
    sentOn: string;
    sentBy: string;
    filerNotes?: string;
    responseReceivedOn: string;
    isDocsUploaded?: boolean;  // for checking if we will display the documents attached and download buttons
    errorTagCodeIds: number[];
    rowNum?: number;
};
export interface ReportTagResponseModel {
    reportTags: ReportTagModel[];
    totalCount: number;
};
export interface ErrorTagCodeModel {
    errorTagCodeId: number;
    errorTagName: string;
};

export interface ReportTagDocument {
    reportTagId: number;
    reportTagDocumentId: number;
    documentUrl: string;
    documentName: string;
    createdByUserId: number;
    createdByUsername: string;
    dateCreated: Date;
};

const ClientRoles = [
    'Client Admin', 'Customer User', 
    'Client Processors', 'State Reporter', 
];

export enum TagStatus {
    All = 'All',
    Resolved = 'Resolved',  // response received
    Unresolved = 'Unresolved',  // no response received
};

const DownloadFileBtn = () => (
    <svg width="20" height="20" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.25 13.75V17.4167C19.25 17.9029 19.0568 18.3692 18.713 18.713C18.3692 19.0568 17.9029 19.25 17.4167 19.25H4.58333C4.0971 19.25 3.63079 19.0568 3.28697 18.713C2.94315 18.3692 2.75 17.9029 2.75 17.4167V13.75M6.41667 9.16667L11 13.75M11 13.75L15.5833 9.16667M11 13.75V2.75" stroke="#02A4E3" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const EditPolicyBtn = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M17 3C17.2626 2.73735 17.5744 2.52901 17.9176 2.38687C18.2608 2.24473 18.6286 2.17157 19 2.17157C19.3714 2.17157 19.7392 2.24473 20.0824 2.38687C20.4256 2.52901 20.7374 2.73735 21 3C21.2626 3.26264 21.471 3.57444 21.6131 3.9176C21.7553 4.26077 21.8284 4.62856 21.8284 5C21.8284 5.37143 21.7553 5.73923 21.6131 6.08239C21.471 6.42555 21.2626 6.73735 21 7L7.5 20.5L2 22L3.5 16.5L17 3Z" stroke="#02A4E3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);


export const ReportTag: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const [loading, setLoading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [reportTags, setReportTags] = useState<ReportTagModel[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportTagModel[]>([]);
    const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
    const [sortModel, setSortModel] = useState<GridSortModel>([]);
    const [addReportTagOpen, setAddReportTagOpen] = React.useState(false);

    const [states, setStates] = useState<{ stateId: number, stateName: string, postalAbbv: string }[]>([{ stateId: 0, stateName: "", postalAbbv: "" }]);
    const [partners, setPartners] = useState<{ partnersId: number, customerName: string }[]>([{ partnersId: 0, customerName: "" }]);
    // use this default data until error tag codes are in the db
    const [errorTagCodes, setErrorTagCodes] = useState<ErrorTagCodeModel[]>([]);
    
    // Document viewer state
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [documentViewerFileName, setDocumentViewerFileName] = useState<string | null>(null);
    const [viewingDocument, setViewingDocument] = useState(false);
    
    // Document list state (for multiple documents)
    const [documentListOpen, setDocumentListOpen] = useState(false);
    const [documentList, setDocumentList] = useState<ReportTagDocument[]>([]);
    const [currentDocumentIndex, setCurrentDocumentIndex] = useState<number>(-1);

    // View tag popup (MESSAGE = filer notes, ATTACHMENTS = documents, no upload)
    type ViewTagPopupState = {
        open: boolean;
        reportTagId: number;
        filerNotes: { filerNote: string; sentBy?: string; sentOn?: string }[];
        documents: { id: string | number; documentName: string; documentUrl: string; createdDate?: string; createdByUsername?: string }[];
    };
    const [viewTagPopup, setViewTagPopup] = useState<ViewTagPopupState>({ open: false, reportTagId: 0, filerNotes: [], documents: [] });
    const [sendMessageToClient, setSendMessageToClient] = useState('');
    const closeViewTagPopup = () => {
        setViewTagPopup({ open: false, reportTagId: 0, filerNotes: [], documents: [] });
        setSendMessageToClient('');
    };
    const openViewTagPopup = (reportTagId: number) => {
        setLoading(true);
        const filerNotesPromise = apiClient(`/ReportTags/getFilerNotes?reportTagId=${reportTagId}`, {
            method: 'GET',
            headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
        }).then((res) => {
            const data = res?.data ?? [];
            return Array.isArray(data) ? data.map((item: { filerNote?: string; sentBy?: string; sentOn?: string }) => ({ filerNote: item.filerNote ?? '', sentBy: item.sentBy, sentOn: item.sentOn })) : [];
        }).catch(() => []);
        const documentsPromise = apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
        }).then((response) => {
            if (!response?.data?.documents?.length) return [];
            return response.data.documents.map((doc: any, index: number) => ({
                id: doc.reportTagDocumentId ?? index,
                documentName: doc.documentName || 'Document',
                documentUrl: doc.documentUrl ?? doc.blobAddress ?? '',
                createdDate: doc.dateCreated ?? doc.createdDate,
                createdByUsername: doc.createdByUsername ?? doc.sentBy ?? 'N/A',
            }));
        }).catch(() => []);
        Promise.all([filerNotesPromise, documentsPromise]).then(([filerNotes, documents]) => {
            setViewTagPopup({ open: true, reportTagId, filerNotes, documents });
        }).finally(() => setLoading(false));
    };
    const handleSendMessageToClient = () => {
        const messageToClient = sendMessageToClient?.trim();
        const reportTagId = viewTagPopup.reportTagId;
        if (!messageToClient || !reportTagId) return;
        setLoading(true);
        apiClient('/ReportTags/sendMessageToClient', {
            method: 'POST',
            headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
            body: JSON.stringify({ reportTagId, messageToClient }),
        }).then((response) => {
            if (response?.status !== false) {
                Swal.fire({ title: 'Message sent', text: 'Your message has been successfully sent to the client.', icon: 'success', confirmButtonText: 'OK' });
                setSendMessageToClient('');
                apiClient(`/ReportTags/getFilerNotes?reportTagId=${reportTagId}`, {
                    method: 'GET',
                    headers: new Headers({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }),
                }).then((res) => {
                    const data = res?.data ?? [];
                    const filerNotes = Array.isArray(data) ? data.map((item: { filerNote?: string; sentBy?: string; sentOn?: string }) => ({ filerNote: item.filerNote ?? '', sentBy: item.sentBy, sentOn: item.sentOn })) : [];
                    setViewTagPopup((prev) => ({ ...prev, filerNotes }));
                }).catch(() => {});
            } else {
                throw new Error(response?.message || 'Failed to send reply');
            }
        }).catch((err) => {
            console.error('Error sending reply to client:', err);
            Swal.fire({ title: 'Error', text: err?.message || 'Failed to send reply to client', icon: 'error', confirmButtonText: 'OK' });
        }).finally(() => setLoading(false));
    };

    // upload documents
    const [uploadDocumentsOpen, setUploadDocumentsOpen] = useState(false);
    const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
    const [clientDocumentData, setClientDocumentData] = useState<{ reportTagId: number, files: File[] }>({ reportTagId: 0, files: [] });

    const IsUserClient = Boolean(user?.userRoles.split(",").some(role => ClientRoles.includes(role)));

    const handleCloseAddReportTag = () => {
        setAddReportTagOpen(false);
        // reload the report data
        getAllReportTags();
        setSelectedRows([]);
    };


    //#region Client Upload Documents
    const uploadDocuments = (reportTagId: number) => {
        setUploadDocumentsOpen(true);
        setClientDocumentData({ reportTagId: reportTagId, files: uploadedDocuments });
    };
    const handleCloseUploadDocuments = () => {
        getAllReportTags();
        setSelectedRows([]);
        setUploadedDocuments([]);
        setUploadDocumentsOpen(false);
        setClientDocumentData({ reportTagId: 0, files: [] });
        
        setUploadDocumentsOpen(false);
        setUploadedDocuments([]);
    };
    const handleUploadDocuments = (files: File[]) => {
        setUploadedDocuments(files);
        setLoading(true);
        const isResolvingInHouse = files.length === 0;
        const formData = new FormData();
        if (!isResolvingInHouse) {
            files.forEach((file) => {
                formData.append('files', file);
            });
        }
        formData.append('data', JSON.stringify({
            reportTagId: clientDocumentData.reportTagId,
            isResolvingInHouse: isResolvingInHouse,
        }));
        Swal.fire({
            title: '<div style="color: white;">Are you sure?</div>',
            html: `<div style="color: white;">${isResolvingInHouse ? 'This will resolve the tag in house' : 'This will send the documents to MISC'}</div>`,
            // text: isResolvingInHouse ? 'This will resolve the tag in house' : 'This will send the documents to MISC',
            showCancelButton: true,
            confirmButtonText: isResolvingInHouse ? 'Yes, resolve it!' : 'Yes, send it!',
            cancelButtonText: 'No, Continue to UPLOAD',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                apiClient('/ReportTags/sendTagToMisc', {
                    method: 'POST',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                    body: formData,
                }).then((response) => {
                    if (!response) return;
                    Swal.fire({
                        title: isResolvingInHouse ? 'Tag Resolved' : 'Documents Sent to MISC',
                        text: isResolvingInHouse ? 'Tag resolved successfully' : 'Documents sent to MISC successfully',
                        icon: 'success',
                        confirmButtonText: 'OK',
                    });
                    // refresh data
                    handleCloseUploadDocuments();
                }).catch((error) => {
                    console.error("Error sending documents to MISC:", error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to send documents to MISC',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                }).finally(() => {
                    setLoading(false);
                });
            }
        });
    };
    //#endregion Client Upload Documents

    const [markAsUnresolvedOpen, setMarkAsUnresolvedOpen] = useState(false);
    const [markAsUnresolvedReportTagId, setMarkAsUnresolvedReportTagId] = useState<number | null>(null);

    const openMarkAsUnresolvedDialog = (reportTagId: number) => {
        setMarkAsUnresolvedReportTagId(reportTagId);
        setMarkAsUnresolvedOpen(true);
    };

    const closeMarkAsUnresolvedDialog = () => {
        setMarkAsUnresolvedOpen(false);
        setMarkAsUnresolvedReportTagId(null);
    };

    const sendTagBackToClient = () => {
        if (markAsUnresolvedReportTagId == null) return;
        setLoading(true);
        const reportTagId = markAsUnresolvedReportTagId;
        apiClient(`/ReportTags/markTagAsUnresolved?reportTagId=${reportTagId}`, {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
        }).then((response) => {
            if (!response) return;
            Swal.fire({
                title: 'Tag Marked as Unresolved',
                text: 'The tag has been marked as unresolved and sent back to the client',
                icon: 'success',
                confirmButtonText: 'OK',
            });
            closeMarkAsUnresolvedDialog();
            getAllReportTags();
            setSelectedRows([]);
        }).catch((error) => {
            console.error("Error marking tag as unresolved:", error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to mark tag as unresolved',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
        });
    };

    const downloadSelectedReportTagDocuments = async (allDocuments: boolean = false, rowsToDownloadOverride?: ReportTagModel[]) => {
        // Determine which rows to download
        let rowsToDownload: ReportTagModel[];
        
        if (rowsToDownloadOverride && rowsToDownloadOverride.length > 0) {
            // Use the provided rows (e.g., from action column click)
            setSelectedRows(rowsToDownloadOverride);
            rowsToDownload = rowsToDownloadOverride;
        } else if (allDocuments) {
            // Use all documents
            rowsToDownload = reportTags;
            
            // Check if there are any documents available
            if (rowsToDownload.length === 0) {
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Available',
                    text: 'There are no documents available to download.',
                });
                return;
            }
        } else {
            // When downloading selected documents, check if any are selected
            // Check both that selectedRows exists and has items
            const hasSelectedRows = selectedRows && Array.isArray(selectedRows) && selectedRows.length > 0;
            if (!hasSelectedRows) {
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Selected',
                    text: 'Please select at least one document to download.',
                });
                return;
            }
            rowsToDownload = selectedRows;
        }
        
        // create a folder with the selected documents
        const folderName = `${rowsToDownload[0].agency} - Report Tag #${rowsToDownload[0].tagNumber} - Documents`;
        
        const confirmResult = await Swal.fire({
            title: '<div style="color: white;">Download Documents</div>',
            html: `<div style="color: white;">This will download all${allDocuments ? '' : ' selected'} documents to a zip file named <strong>${folderName}.zip</strong></div>`,
            showCancelButton: true,
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        });
        
        if (!confirmResult.isConfirmed) {
            setSelectedRows([]);
            return;
        }
        
        setLoading(true);
        
        // Show loading dialog
        Swal.fire({
            title: 'Preparing Download...',
            html: 'Please wait while we prepare your files for download.',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });
        
        try {
            const zip = new JSZip();
            let successCount = 0;
            let failCount = 0;
            const errors: string[] = [];
            const fetchErrors: string[] = [];

            // Fetch documents for all selected rows
            const allDocuments: ReportTagDocument[] = [];
            
            // Fetch documents for each report tag
            await Promise.all(
                rowsToDownload.map(async (row) => {
                    if (!row.reportTagId) return;
                    
                    try {
                        const response = await apiClient(`/ReportTags/getReportTagDocumentsById/${row.reportTagId}`, {
                            method: 'GET',
                            headers: new Headers({
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                            })
                        });
                        
                        if (response && response.data && response.data.documents && response.data.documents.length > 0) {
                            const documents: ReportTagDocument[] = response.data.documents.map((doc: any) => ({
                                reportTagId: doc.reportTagId || 0,
                                reportTagDocumentId: doc.reportTagDocumentId || 0,
                                documentUrl: doc.documentUrl,
                                documentName: doc.documentName || 'Document',
                                createdByUserId: doc.createdByUserId || 0,
                                createdByUsername: doc.createdByUsername || 'Unknown',
                                dateCreated: new Date(doc.dateCreated) || '',
                            }));
                            allDocuments.push(...documents);
                        }
                    } catch (error) {
                        console.error(`Error fetching documents for reportTagId ${row.reportTagId}:`, error);
                        fetchErrors.push(`Tag #${row.tagNumber}`);
                    }
                })
            );

            if (allDocuments.length === 0) {
                Swal.close();
                const errorMessage = fetchErrors.length > 0 
                    ? `No documents were found. Failed to fetch documents for: ${fetchErrors.join(', ')}`
                    : 'No documents were found for the selected tags.';
                Swal.fire({
                    icon: 'error',
                    title: 'No Documents Found',
                    text: errorMessage,
                    confirmButtonText: 'OK',
                });
                return;
            }
            
            const usedFileNames = new Set<string>();
            // Helper function to generate unique filename (Chrome-style numbering)
            const generateUniqueFileName = (baseFileName: string): string => {
                const fileExtension = baseFileName.includes('.') 
                    ? baseFileName.substring(baseFileName.lastIndexOf('.'))
                    : '';
                const fileNameWithoutExt = baseFileName.includes('.')
                    ? baseFileName.substring(0, baseFileName.lastIndexOf('.'))
                    : baseFileName;
                
                let uniqueFileName = baseFileName;
                let counter = 0;
                
                // Keep trying until we find a unique name
                while (usedFileNames.has(uniqueFileName)) {
                    counter++;
                    uniqueFileName = `${fileNameWithoutExt} (${counter})${fileExtension}`;
                }
                
                usedFileNames.add(uniqueFileName);
                return uniqueFileName;
            };
            
            // Generate unique filenames for all files first
            const filesWithUniqueNames = allDocuments.map((item: ReportTagDocument) => ({
                item,
                uniqueFileName: generateUniqueFileName(item.documentName || `document_${item.reportTagDocumentId}`)
            }));
            
            // Fetch each file and add to zip
            await Promise.all(
                filesWithUniqueNames.map(async ({ item, uniqueFileName }) => {
                    try {
                        // Fetch the file from documentUrl
                        const response = await fetch(item.documentUrl, {
                            method: 'GET',
                            mode: 'cors',
                            credentials: 'omit',
                            cache: 'no-cache',
                        });
                        
                        if (!response.ok) {
                            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                        }
                        
                        // Get the blob (preserves original content type)
                        const blob = await response.blob();
                        
                        // Add file to zip
                        zip.file(uniqueFileName, blob);
                        successCount++;
                    } catch (error: any) {
                        failCount++;
                        const errorMsg = error.message || 'Failed to download';
                        errors.push(`${item.documentName}: ${errorMsg}`);
                        console.error(`Error downloading ${item.documentName} from ${item.documentUrl}:`, error);
                        console.error('Error details:', error);
                    }
                })
            );

            if (successCount === 0) {
                throw new Error('Failed to download any documents');
            }

            // Generate zip file
            const zipBlob = await zip.generateAsync({ type: 'blob' });
            
            // Create download link
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${folderName}.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            Swal.close();
            
            // Show success/partial success message
            const hasFetchErrors = fetchErrors.length > 0;
            const hasDownloadErrors = failCount > 0;
            
            if (hasFetchErrors || hasDownloadErrors) {
                Swal.fire({
                    icon: 'warning',
                    title: 'Partial Success',
                    html: `
                        <p><strong>${successCount}</strong> document(s) downloaded successfully.</p>
                        ${hasDownloadErrors ? `<p><strong>${failCount}</strong> document(s) failed to download.</p>` : ''}
                        ${hasFetchErrors ? `<p><strong>${fetchErrors.length}</strong> tag(s) failed to fetch documents: ${fetchErrors.join(', ')}</p>` : ''}
                        ${errors.length > 0 ? `<p style="font-size: 12px; color: #666; margin-top: 10px;">Download Errors:<br>${errors.slice(0, 3).join('<br>')}${errors.length > 3 ? '<br>...' : ''}</p>` : ''}
                    `,
                    confirmButtonText: 'OK',
                });
            } else {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `All ${successCount} document(s) have been downloaded successfully.`,
                    confirmButtonText: 'OK',
                });
            }
        } catch (error: any) {
            console.error('Error in downloadSelectedReportTagDocuments:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.message || 'An error occurred while downloading the documents.',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
            setSelectedRows([]);
        }
    };

    const filterOperators = getGridStringOperators().filter(({ value }) => ['contains'].includes(value));
    const columns: GridColDef[] = [
        { field: 'agency', headerName: 'Client',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string',
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators
        },
        { field: 'state', headerName: 'State',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string',
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators
        },
        { field: 'invoiceNumber', headerName: 'Invoice Number',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string',
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators,
            renderCell: (params) => (
                <div>{params.row.isMultipleTags ? '' : (params.value ?? '')}</div>
            ),
        },
        { field: 'policyNumber', headerName: 'Policy Number',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string',
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators,
            renderCell: (params) => (
                <div>{params.row.isMultipleTags ? '' : (params.value ?? '')}</div>
            ),
        },
        { field: 'insuredName', headerName: 'Insured Name',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string',
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators,
            renderCell: (params) => (
                <div>{params.row.isMultipleTags ? '' : (params.value ?? '')}</div>
            ),
        },
        { field: 'tagNumber', headerName: 'Tag Number',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string',
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators,
            valueGetter: (params) => (params.value != null ? String(params.value) : ''),
        },
        { field: 'filerNotes', headerName: 'Filer Notes',
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string',
            flex: 1, minWidth: 100, align: 'center', headerAlign: 'center', filterOperators,
            renderCell: (params) => (
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 200, textAlign: 'left' }}>
                    {(params.value && String(params.value).trim()) ? params.value : ''}
                </div>
            ),
        },
        { field: 'sentOn', headerName: 'Sent On', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators
        },
        { field: 'sentBy', headerName: 'Sent By', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.5, minWidth: 80, align: 'center', headerAlign: 'center', filterOperators
        },
        ...(!IsUserClient ? [{
            field: 'responseReceivedOn', headerName: 'Response Received', 
            hideSortIcons: true,
            renderHeader: (params: any) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), type: 'string', 
            flex: 0.8, minWidth: 80, align: 'center' as const, 
            headerAlign: 'center' as const, filterOperators
        }] : []),
        { field: 'Action', headerName: 'Action', flex: 0.8, minWidth: 160, filterable: false, 
            headerAlign: 'center', align: 'left', disableExport: true,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.0, justifyContent: 'flex-start' }}>
                    {params.row.filerNotes && params.row.filerNotes.trim() !== "" && (
                        <Tooltip arrow={true}
                            title={
                                <div style={{
                                    maxWidth: "320px",
                                    fontSize: "16px",
                                    textAlign: "left",
                                    whiteSpace: "pre-line"
                                }}>{params.row.filerNotes}</div>
                            }
                        ><InfoIcon fontSize="small" style={{ color: 'grey' }} /></Tooltip>
                    )}
                    {(!IsUserClient && params.row.responseReceivedOn !== 'N/A') && (
                        <Tooltip title="Mark as Unresolved" arrow={true}>
                            <IconButton title="Mark as Unresolved"
                                onClick={() => {
                                    openMarkAsUnresolvedDialog(params.row.reportTagId!);
                                }}
                                sx={{ color: LightBlueBtnColor }}
                            ><EditPolicyBtn /></IconButton>
                        </Tooltip>
                    )}
                    {IsUserClient && (
                        <Tooltip title="Upload Documents" arrow={true}>
                            <IconButton sx={{ color: LightBlueBtnColor }}
                                onClick={() => {
                                    uploadDocuments(params.row.reportTagId);
                                }}
                            ><FaUpload /></IconButton>
                        </Tooltip>
                    )}
                    {params.row.isDocsUploaded && (<>
                        <Tooltip title="View Documents Attached" arrow={true}>
                            <IconButton sx={{ color: LightBlueBtnColor }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (params.row.reportTagId) {
                                        openViewTagPopup(params.row.reportTagId);
                                    }
                                }}
                            ><FaRegEye /></IconButton>
                        </Tooltip>
                        <Tooltip title="Download all Documents Attached" arrow={true}>
                            <IconButton sx={{ color: LightBlueBtnColor }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    downloadSelectedReportTagDocuments(false, [params.row]);
                                }}
                            ><FaDownload /></IconButton>
                        </Tooltip>
                        
                    </>)}
                </Box>
            )
        }
    ];

    useEffect(() => {
        fetchPartnersData();
        fetchStates();
        fetchErrorTagCodes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        getAllReportTags();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, filterModel, sortModel]);
     
    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const reportTagIdParam = searchParams.get('reportTagId');
        const openAttachments = searchParams.get('openAttachments') === 'true';
 
        if (reportTagIdParam && openAttachments) {
            const id = parseInt(reportTagIdParam, 10);
            if (!isNaN(id) && id > 0) {
                window.history.replaceState({}, '', window.location.pathname);
                viewDocumentsAttached(id);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
 
    const fetchPartnersData = () => {
        setLoading(true);
        apiClient('/Partners/getPartners', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setPartners(responseJson.data);
        }).catch(error => {
            console.error("Error fetching partners:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const fetchStates = () => {
        setLoading(true);
        apiClient('/States/getStates', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setStates(responseJson.data);
        }).catch(error => {
            console.error("Error fetching states:", error)
        }).finally(() => {
            setLoading(false);
        });
    };
    const getAllReportTags = () => {
        setLoading(true);
        
        // Convert filterModel to the format expected by the API
        const filterModelJson = filterModel && filterModel.items && filterModel.items.length > 0 ? filterModel : null;
        
        // Get sort field and direction from sortModel
        const sortField = sortModel && sortModel.length > 0 ? sortModel[0].field || '' : '';
        const sortDirection = sortModel && sortModel.length > 0 ? sortModel[0].sort || '' : '';
        
        apiClient('/ReportTags/getAllReportTags', {
            method: 'POST',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            }),
            body: JSON.stringify({
                pageSize: pageSize,
                pageNumber: currentPage,
                field: sortField,
                sort: sortDirection,
                filterModel: filterModelJson,
                ...(IsUserClient && { partnerId: user?.partnersId }),
            })
        }).then((responseJson) => {
            if (!responseJson || !responseJson.data) return;
            // Server-side filtering handles IsUserClient filtering, so no client-side filtering needed
            setReportTags(responseJson.data.reportTags);
            setTotalCount(responseJson.data.totalCount);
        }).catch(error => {
            console.error("Error fetching report tags:", error);
            setReportTags([]);
            setTotalCount(0);
        }).finally(() => {
            setLoading(false);
        });
    };
    const fetchErrorTagCodes = () => {
        setLoading(true);
        apiClient('/ReportTags/getAllErrorTags', {
            method: "GET",
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((responseJson) => {
            if (!responseJson) return;
            setErrorTagCodes(responseJson.data);
        }).catch(error => {
            console.error("Error fetching error tag codes:", error)
        }).finally(() => {
            setLoading(false);
        });
    };

    const viewDocumentsAttached = (reportTagId: number) => {
        setDocumentListOpen(true);
        setLoading(true);
        apiClient(`/ReportTags/getReportTagDocumentsById/${reportTagId}`, {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            })
        }).then((response) => {
            if (!response) return;
            const { data } = response;
            if (data && data.documents && data.documents.length > 0) {
                const documents: ReportTagDocument[] = data.documents.map((doc: any) => ({
                    reportTagId: doc.reportTagId || 0,
                    reportTagDocumentId: doc.reportTagDocumentId || 0,
                    documentUrl: doc.documentUrl,
                    documentName: doc.documentName || 'Document',
                    createdByUserId: doc.createdByUserId || 0,
                    createdByUsername: doc.createdByUsername || 'Unknown',
                    dateCreated: new Date(doc.dateCreated) || '',
                }));
                setDocumentList(documents);
            } else {
                setDocumentListOpen(false);
                Swal.fire({
                    title: 'No Documents',
                    text: 'No documents found for this tag',
                    icon: 'info',
                    confirmButtonText: 'OK',
                });
            }
        }).catch((error) => {
            console.error("Error fetching documents:", error);
            setDocumentListOpen(false);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch documents',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        }).finally(() => {
            setLoading(false);
        });
    };
    const deleteReportTagDocument = (document: ReportTagDocument) => {
        setLoading(true);
        Swal.fire({
            title: '<div style="color: white;">Are you sure?</div>',
            html: `<div style="color: white;">This will delete the document</div>`,
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, Cancel',
            confirmButtonColor: GreenBtnColor,
            cancelButtonColor: OrangeBtnColor,
            reverseButtons: true,
            background: PrimaryBlueColor,
        }).then((result) => {
            if (result.isConfirmed) {
                apiClient(`/ReportTags/deleteReportTagDocument?reportTagId=${document.reportTagId}&reportTagDocumentId=${document.reportTagDocumentId}`, {
                    method: 'DELETE',
                    headers: new Headers({
                        'Authorization': `Bearer ${token}`,
                    }),
                }).then((response) => {
                    if (!response) return;
                    Swal.fire({
                        title: 'Document deleted successfully',
                        text: 'The document has been deleted successfully',
                        icon: 'success',
                        confirmButtonText: 'OK',
                    });
                    // remove document from document list
                    setDocumentList(documentList.filter(doc => doc.reportTagDocumentId !== document.reportTagDocumentId));
                    // refresh the document list
                    handleCloseDocumentViewer();
                    viewDocumentsAttached(document.reportTagId);

                }).catch((error) => {
                    console.error("Error deleting document:", error);
                    Swal.fire({
                        title: 'Error',
                        text: 'Failed to delete document',
                        icon: 'error',
                        confirmButtonText: 'OK',
                    });
                }).finally(() => {
                    setLoading(false);
                });
            }
        }).finally(() => {
            setLoading(false);
        });
    };
    
    const handleCloseDocumentList = () => {
        setDocumentListOpen(false);
        // Only clear if viewer is not open (user closed list without viewing)
        if (!documentViewerOpen) {
            setDocumentList([]);
            setCurrentDocumentIndex(-1);
        }
        if (window.location.search) {
            window.history.replaceState({}, '', window.location.pathname);
        }
    };
    const handleSelectDocument = (document: ReportTagDocument, index: number) => {
        setCurrentDocumentIndex(index);
        setDocumentListOpen(false); // Close list but keep documents in state
        handleViewDocument(document.documentUrl, document.documentName);
    };
    
    const handleBackToList = () => {
        // Close viewer and show list again
        setDocumentViewerOpen(false);
        if (documentViewerUrl) {
            URL.revokeObjectURL(documentViewerUrl);
        }
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setViewingDocument(false);
        // Show the list dialog if there are multiple documents
        if (documentList.length > 1) {
            setDocumentListOpen(true);
        }
    };
    
    const handlePreviousDocument = () => {
        if (currentDocumentIndex > 0 && documentList.length > 0) {
            const newIndex = currentDocumentIndex - 1;
            setCurrentDocumentIndex(newIndex);
            handleViewDocument(documentList[newIndex].documentUrl, documentList[newIndex].documentName);
        }
    };
    
    const handleNextDocument = () => {
        if (currentDocumentIndex < documentList.length - 1 && documentList.length > 0) {
            const newIndex = currentDocumentIndex + 1;
            setCurrentDocumentIndex(newIndex);
            handleViewDocument(documentList[newIndex].documentUrl, documentList[newIndex].documentName);
        }
    };
    const handleViewDocument = (blobAddress: string, fileName?: string) => {
        if (!blobAddress) {
            Swal.fire({
                title: 'Error',
                text: 'No document URL available',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            return;
        }

        // Revoke previous blob URL if it exists to prevent memory leaks
        if (documentViewerUrl) {
            URL.revokeObjectURL(documentViewerUrl);
        }

        // Open dialog immediately to show loading
        setViewingDocument(true);
        setDocumentViewerOpen(true);
        setDocumentViewerFileName(fileName || "document.pdf");

        try {
            const blobUrl = blobAddress;
            // Set the blob URL to display in the viewer
            setDocumentViewerUrl(blobUrl);
            setViewingDocument(false);
        } catch (error) {
            console.error("Error downloading document:", error);
            setViewingDocument(false);
            setDocumentViewerOpen(false);
            // If download fails, show error and fall back to opening in new tab
            Swal.fire({
                title: "Error",
                text: "Failed to download file. Opening in new tab instead.",
                icon: "warning",
                confirmButtonText: "OK",
            }).then(() => {
                window.open(blobAddress, "_blank");
            });
        }
    };
    const handleCloseDocumentViewer = () => {
        if (documentViewerUrl) {
            URL.revokeObjectURL(documentViewerUrl);
        }
        setDocumentViewerOpen(false);
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setViewingDocument(false);
        // Clear document list and index when fully closing
        setDocumentList([]);
        setCurrentDocumentIndex(-1);
    };

    /** Open DocumentViewer from View Tag popup with only the selected document. */
    const openViewTagAttachmentDocument = (row: { documentUrl: string; documentName: string }) => {
        if (!row.documentUrl || !row.documentName) return;
        const singleDoc: ReportTagDocument = {
            reportTagId: 0,
            reportTagDocumentId: 0,
            documentUrl: row.documentUrl,
            documentName: row.documentName,
            createdByUserId: 0,
            createdByUsername: '',
            dateCreated: new Date(),
        };
        setDocumentList([singleDoc]);
        setCurrentDocumentIndex(0);
        setDocumentViewerOpen(true);
    };

    const addNewTag = () => {
        setSelectedRows([]);
        setAddReportTagOpen(true);
    };

    const deleteSelectedTags = () => {
        if(selectedRows.length === 0) {
            Swal.fire({
                title: 'No tags selected',
                text: "Please select at least one tag to delete",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#02A4E3',
                cancelButtonColor: '#d33',
                confirmButtonText: 'OK',
            });
        }else{
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: `<div style="color: white;">You won't be able to revert this!</div>`,
                showCancelButton: true,
                confirmButtonText: 'Yes, Delete',
                cancelButtonText: 'No, Continue',
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                background: PrimaryBlueColor,
            }).then((result) => {
                if(result.isConfirmed) {
                    setLoading(true);
                    const tagIdList = selectedRows.map(row => row.reportTagId).join(',');
                    apiClient(`/ReportTags/delete?tagIdList=${tagIdList}`, {
                        method: 'DELETE',
                        headers: new Headers({
                            'Authorization': `Bearer ${token}`,
                        }),
                    }).then((response) => {
                        setLoading(false);
                        
                        // delete logic here
                        if(response.status){
                            // reload the report data
                            getAllReportTags();
                            setSelectedRows([]);
                            Swal.fire({
                                title: 'Tags deleted successfully',
                                text: "The tags have been deleted successfully",
                                icon: 'success',
                                showCancelButton: true,
                                confirmButtonColor: '#02A4E3',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'OK',
                            });
                        }else{
                            Swal.fire({
                                title: 'Error',
                                text: "Failed to delete tags",
                                icon: 'error',
                                showCancelButton: true,
                                confirmButtonColor: '#02A4E3',
                                cancelButtonColor: '#d33',
                                confirmButtonText: 'OK',
                            });
                        }
                    }).catch((error) => {
                        setLoading(false);
                        Swal.fire({
                            title: 'Error',
                            text: "Failed to delete tags",
                            icon: 'error',
                            showCancelButton: true,
                            confirmButtonColor: '#02A4E3',
                            cancelButtonColor: '#d33',
                            confirmButtonText: 'OK',
                        });
                    });
                }
            });
        }
    };
    
    const downloadDocument = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, doc: ReportTagDocument) => {
        e.stopPropagation();
        try {
            setLoading(true);
            const response = await fetch(doc.documentUrl, {
                method: 'GET',
                mode: 'cors',
                credentials: 'omit',
                cache: 'no-cache',
            });
            
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = doc.documentName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error: any) {
            console.error('Error downloading document:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to download document',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
        }
    };

    //#region UI Elements
    const getDetailPanelHeight = useCallback(() => 200, []);
    const getDetailPanelContent = useCallback<NonNullable<DataGridProProps['getDetailPanelContent']>>(({ row }) => (
        <DetailPanelContent row={row} />
    ), [errorTagCodes]);
    function DetailPanelContent({ row }: { row: ReportTagModel }){
        const errorTagRows = row.errorTagCodeIds?.map(id => {
            const errorTag = errorTagCodes.find(code => code.errorTagCodeId === id);
            return errorTag ? errorTag : null;
        }).filter((item): item is { errorTagCodeId: number; errorTagName: string } => item !== null) ?? [];
        return (
            <Stack direction="column" spacing={1} sx={{ height: 1 }} className="hide-border-12">
                <DataGridPro 
                    columns={[
                        {
                            field: 'errorTagName', headerName: 'Error Type', 
                            type: 'string',
                            flex: 1,
                            headerAlign: 'center',
                            align: 'center',
                            hideSortIcons: true,
                            renderHeader: (params) => (
                                <div>
                                    <span className="table-card-title">{params.colDef.headerName}</span>
                                    <RiExpandUpDownLine />
                                </div>
                            ),
                        }
                    ]}
                    rows={errorTagRows}
                    getRowId={(r) => r?.errorTagCodeId ?? 0}
                    sx={{ flex: 1 }}
                    hideFooter
                />
            </Stack>
        );
    };
    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarExport className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" />
            </GridToolbarContainer>
        );
    };
    function EditToolbar() {
        return (
            <div className=" p-2 d-flex justify-content-end datagrid-butons">
                <div className="ml-2"><CustomToolbar /></div>
            </div>
        );
    };
    //#endregion

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="card-title">
                    <span className="d-flex">{IsUserClient ? 'Reported Error Tags' : 'I Want To... Report a Tag'}</span>
                </div>
            </div>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: 99999, width: "100%", pointerEvents: "none" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>
            <Box sx={{ width: "100%" }}>
                {!IsUserClient && (
                <Box sx={{ width: 'auto', display: 'flex', gap: 1, mb: 2 }}>
                    <Box sx={{ minWidth: '150px', transform: 'scale(0.95)', transformOrigin: 'left center' }}>
                        <DashboardButton title="New Tag" 
                            onClick={addNewTag} 
                            icon={<FaPlus />} 
                            variant="primary" 
                        />
                    </Box>
                    <Box sx={{ minWidth: '150px', transform: 'scale(0.95)', transformOrigin: 'left center' }}>
                        <DashboardButton title="Delete" 
                            onClick={deleteSelectedTags} 
                            icon={<FaTrash />} 
                            variant="secondary" 
                        />
                    </Box>
                </Box>
                )}
                <Box overflow={"scroll"}>
                    <Box sx={{ p: 3 }}>
                        <div style={{ height: "59vh", display: "flex", flexDirection: "column", width: "100%" }}>
                            {loading && <LinearProgress sx={{ backgroundColor: "#132D3D" }} />}
                            <div style={{ flex: 1, overflow: "auto" }}>
                                <DataGridPro
                                    rows={reportTags}
                                    getRowId={(r) => r?.reportTagId ?? 0}
                                    columns={columns}
                                    getDetailPanelHeight={getDetailPanelHeight}
                                    getDetailPanelContent={getDetailPanelContent}

                                    page={currentPage - 1}
                                    pageSize={pageSize}
                                    pagination
                                    rowCount={totalCount}
                                    paginationMode="server"
                                    onPageChange={(newPage) => setCurrentPage(newPage + 1)}
                                    onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                                    rowsPerPageOptions={getRowsPerPageOptions(totalCount, pageSize)}
                                    filterMode="server"
                                    filterModel={filterModel}
                                    onFilterModelChange={(newFilterModel) => {
                                        setFilterModel(newFilterModel);
                                        setCurrentPage(1); // Reset to first page when filter changes
                                    }}
                                    sortingMode="server"
                                    sortingOrder={['asc', 'desc']}
                                    sortModel={sortModel}
                                    onSortModelChange={(newSortModel) => {
                                        setSortModel(newSortModel);
                                        setCurrentPage(1); // Reset to first page when sort changes
                                    }}
                                    checkboxSelection={true}
                                    selectionModel={selectedRows.map(row => row.reportTagId).filter((id): id is number => id !== undefined)}
                                    onSelectionModelChange={(newSelectionModel) => {
                                        // Map the selected IDs to full ReportTagModel objects
                                        const selectedObjects = (newSelectionModel as number[])
                                            .map(id => reportTags.find(tag => tag.reportTagId === id))
                                            .filter((tag): tag is ReportTagModel => tag !== undefined);
                                        setSelectedRows(selectedObjects);
                                    }}
                                    
                                    components={{
                                        Toolbar: EditToolbar,
                                    }}
                                    sx={{
                                        border: '0px',
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
            </Box>
            <Box sx={{ width: '80%' }}>
                <AddReportTag Open={addReportTagOpen} handleClose={handleCloseAddReportTag} 
                    partners={partners} 
                    states={states} 
                    errorTagCodes={errorTagCodes} 
                />
            </Box>
            
            {/* Document List Dialog (for multiple documents) */}
            <ViewReportTagAttachments open={documentListOpen} 
                handleClose={handleCloseDocumentList} 
                documentList={documentList} 
                loading={loading} 
                IsUserClient={IsUserClient} 
                handleSelectDocument={handleSelectDocument} 
                downloadDocument={downloadDocument}
            />
            
            {/* Document Viewer Dialog */}
            <DocumentViewer open={documentViewerOpen}
                documents={documentList}
                onClose={handleBackToList}
            />
            
            {/* Upload Documents Dialog */}
            <UploadReportTagDocuments 
                uploadDocumentsOpen={uploadDocumentsOpen}
                handleCloseUploadDocuments={handleCloseUploadDocuments}
                reportTag={reportTags.find(tag => tag.reportTagId === clientDocumentData.reportTagId) as ReportTagModel}
                clientDocumentData={clientDocumentData}
                actionFunction={() => {
                    getAllReportTags();
                    setSelectedRows([]);
                    setUploadedDocuments([]);
                    setUploadDocumentsOpen(false);
                    setClientDocumentData({ reportTagId: 0, files: [] });
                }}
            />

            {/* View Tag popup – MESSAGE (filer notes) + ATTACHMENTS History (no upload) */}
            <Dialog open={viewTagPopup.open} onClose={closeViewTagPopup} fullWidth maxWidth="xl" PaperProps={{ style: { width: '98%', maxWidth: 1600, maxHeight: '90vh', margin: 'auto' } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', backgroundColor: '#e8f5fe' }}>
                    <DialogTitle style={{ color: '#00A9E0', fontSize: '25px' }}>Report Tag</DialogTitle>
                </Box>
                <DialogContent style={{ backgroundColor: '#f4f9fc', padding: '24px 24px 16px' }}>
                    <div style={{ display: 'flex', gap: '20px', flex: 1, minHeight: '400px', alignItems: 'stretch' }}>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', marginBottom: 12, marginLeft: 0,marginTop: 0, fontWeight: 'bold', fontSize: '20px', lineHeight: 1.3 }}>MESSAGE</h3>
                            <div style={{ flex: 1, minHeight: 500, maxHeight: 320, overflowY: 'auto', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                {viewTagPopup.filerNotes.length > 0 ? (
                                    viewTagPopup.filerNotes.map((item, index) => (
                                        <Box key={index} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
                                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                                                {[item.sentBy, item.sentOn && safeMoment(item.sentOn).format('MM/DD/YYYY hh:mm A')].filter(Boolean).join(' • ')}
                                            </Typography>
                                            <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: 'text.primary' }}>
                                                {item.filerNote}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ color: '#666' }}>No messages available</Typography>
                                )}
                            </div>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <h3 style={{ color: '#00A9E0', fontWeight: 'bold', fontSize: '20px', margin: 0, marginBottom: 12, lineHeight: 1.3 }}>ATTACHMENTS</h3>
                            <div style={{ flex: 1, minHeight: 360, display: 'flex', flexDirection: 'column', border: '1px solid #ddd', padding: '16px', borderRadius: '4px', backgroundColor: '#fff' }}>
                                <h3 style={{ color: '#000', fontWeight: 'bold', fontSize: '16px', marginBottom: 12, marginLeft: 0 }}>History</h3>
                                <div style={{ width: '100%' }}>
                                    {viewTagPopup.documents.length > 0 ? (
                                        <DataGridPro
                                            rows={viewTagPopup.documents}
                                            getRowId={(r) => String(r?.id ?? r?.documentUrl ?? '')}
                                            autoHeight
                                            columns={[
                                                { field: 'documentName', headerName: 'File Name', flex: 2, minWidth: 180, sortable: false, renderCell: (params) => <Typography variant="body2" sx={{ color: 'text.primary' }}>{params.value || 'Document'}</Typography> },
                                                { field: 'createdByUsername', headerName: 'Uploaded By', flex: 1, minWidth: 120 },
                                                { field: 'createdDate', headerName: 'Upload Date', flex: 1, minWidth: 130, renderCell: (params) => (params.value ? safeMoment(params.value).format('MM/DD/YYYY HH:mm') : 'N/A') },
                                                {
                                                    field: 'action',
                                                    headerName: 'Actions',
                                                    flex: 0.6,
                                                    minWidth: 90,
                                                    sortable: false,
                                                    renderCell: (params) => (
                                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                            <Tooltip title="View" arrow>
                                                                <IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={() => openViewTagAttachmentDocument(params.row)}>
                                                                    <FaRegEye />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Download" arrow>
                                                                <IconButton size="small" sx={{ color: LightBlueBtnColor }} onClick={(e) => { e.stopPropagation(); if (params.row.documentUrl && params.row.documentName) { const doc = { documentUrl: params.row.documentUrl, documentName: params.row.documentName } as ReportTagDocument; downloadDocument(e, doc); } }}><FaDownload /></IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    ),
                                                },
                                            ]}
                                            pagination
                                            pageSize={5}
                                            rowsPerPageOptions={[5]}
                                            disableSelectionOnClick
                                            sx={{ border: 0 }}
                                            components={{ NoRowsOverlay: () => <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography> }}
                                        />
                                    ) : (
                                        <Typography sx={{ color: '#666', textAlign: 'center', py: 3 }}>No documents available</Typography>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Send Message</Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            placeholder="Enter your message..."
                            value={sendMessageToClient}
                            onChange={(e) => setSendMessageToClient(e.target.value)}
                            size="small"
                            sx={{ mb: 1.5 }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                            <Button onClick={closeViewTagPopup} variant="outlined" sx={{ backgroundColor: '#E0E0E0', color: '#000', borderColor: '#ccc' }}>Cancel</Button>
                            <Button onClick={handleSendMessageToClient} variant="contained" disabled={!sendMessageToClient?.trim()} sx={{ backgroundColor: '#1976D2', color: '#fff', textTransform: 'none', fontWeight: 600 }}>Send</Button>
                        </div>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Mark as Unresolved Dialog */}
            <Dialog open={markAsUnresolvedOpen} onClose={closeMarkAsUnresolvedDialog} maxWidth="sm" fullWidth>
                <DialogTitle className="card-title" sx={{ textAlign: 'center' }}>Are You Sure?</DialogTitle>
                <DialogContent>
                    <Typography>Do you want to send the tag back to client?</Typography>
                </DialogContent>
                <DialogActions sx={{ alignItems: 'center', justifyContent: 'center', '& button': { minHeight: 40 } }}>
                    <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={closeMarkAsUnresolvedDialog}>Cancel</button>
                    <button className="btn-2 btn d-flex btn-primary  active-btn-blue search-btn" type="button" onClick={sendTagBackToClient}>
                        Mark tag as Unresolved
                    </button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};
interface ViewReportTagAttachmentsProps {
    open: boolean;
    handleClose: () => void;
    documentList: ReportTagDocument[];
    loading: boolean;
    IsUserClient: boolean;
    handleSelectDocument: (doc: ReportTagDocument, index: number) => void;
    downloadDocument: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, doc: ReportTagDocument) => void;
};
const ViewReportTagAttachments: React.FC<ViewReportTagAttachmentsProps> = ({ open, handleClose, documentList, loading, IsUserClient, handleSelectDocument, downloadDocument }) => {
    const closeAttachmentsDialog = () => {
        handleClose();
    };
    
    const columns: GridColDef[] = [
        { field: 'documentName', headerName: 'Document Name', 
            flex: 3, minWidth: 250, align: 'left', headerAlign: 'left', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
        },
        { field: 'dateCreated', headerName: 'Date Uploaded', 
            flex: 0.8, minWidth: 130, align: 'center', headerAlign: 'center', type: 'date', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ), 
            renderCell: (params) => (
                <div>
                    {params.value ? safeMoment(params.value).format('MM-DD-YYYY hh:mm A') : 'N/A'}
                </div>
            )
        },
        {
            field: 'createdByUsername', headerName: 'Uploaded By', 
            flex: 0.8, minWidth: 130, align: 'center', headerAlign: 'center', type: 'string', 
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
        },
        { field: 'action', headerName: 'Download', 
            hideSortIcons: true,
            type: 'string', 
            flex: 0.6, minWidth: 120, align: 'left', headerAlign: 'center', 
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'left', justifyContent: 'left' }}>
                    <Tooltip title="View Document" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.documentUrl && params.row.documentName) {
                                    handleSelectDocument(params.row, 0);
                                }
                            }} 
                        ><FaRegEye /></IconButton>
                    </Tooltip>
                    <Tooltip title="Download Documents" arrow={true}>
                        <IconButton sx={{ color: LightBlueBtnColor }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (params.row.reportTagId) {
                                    downloadDocument(e, params.row);
                                }
                            }}
                        ><FaDownload /></IconButton>
                    </Tooltip>
                </Box>
            )
        },
    ];
    
    return (
        <Dialog open={open} onClose={closeAttachmentsDialog} maxWidth="lg" fullWidth>
            <DialogTitle className="card-title">Select Document to view</DialogTitle>
            <DialogContent>
                <Box overflow={"scroll"}>
                    <Box sx={{ p: 3 }}>
                        <div style={{ height: "59vh", display: "flex", flexDirection: "column", width: "100%" }}>
                            <div style={{ flex: 1, overflow: "auto" }}>
                                <DataGridPro rows={documentList}
                                    getRowId={(r) => r?.reportTagDocumentId ?? 0}
                                    columns={columns}
                                    sx={{
                                        border: '0px',
                                    }}
                                />
                            </div>
                        </div>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <button className="btn-2 btn d-flex btn-primary search-btn" type="button" onClick={closeAttachmentsDialog}>Close</button>
            </DialogActions>
        </Dialog>
    );
};
export default ReportTag;